<script module lang="ts">
  import { reactiveContext } from "#lib/internal/helpers"
  import { collectionContext } from "#lib/internal/helpers/collectionContext"
  import { useActions } from "#lib/internal/helpers/useActions"

  type Type = "single" | "multiple"

  type SingleAccordionRootProps = {
    type?: "single"
    value?: string | number | null
  }

  type MultipleAccordionRootProps = {
    type: "multiple"
    value?: string[] | number[]
  }

  export type AccordionRootProps = BaseProps<"div"> & {
    disabled?: boolean
    use?: any[]
  } & (SingleAccordionRootProps | MultipleAccordionRootProps)

  export type AccordionContext = {
    readonly type: Type
    readonly disabled: boolean
    value: AccordionRootProps["value"]
  }

  const { getContext, setContext, defaults } =
    reactiveContext<AccordionContext>({
      type: "single",
      value: null,
      disabled: false,
    })
  export const getRootCtx = getContext

  // Create a context for all trigger components
  const triggerCollectionContext = collectionContext()
  export const getTriggerCollection = triggerCollectionContext.getContext
</script>

<script lang="ts">
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import type { Snippet } from "svelte"

  type Props = AccordionRootProps & {
    children?: Snippet
  }

  let {
    type = defaults?.type,
    value = $bindable(defaults?.value),
    disabled = defaults?.disabled,
    class: className = undefined,
    use = [],
    children,
    ...restProps
  }: Props = $props()

  $effect(() => {
    value = value?.toString() ?? null
  })

  const ctx = setContext({
    value: (v) => {
      const next = v ?? null
      if (next !== value) value = next
    },
  })
  $effect(() => {
    ctx.set({ type, value, disabled })
  })

  triggerCollectionContext.setContext()
</script>

<div {...restProps} use:useActions={use} class={cn("w-full", className)}>
  {@render children?.()}
</div>
